import { useEffect, useState } from "react";
import type { DeviceResponse, DevicesResponse } from "../api/contract.gen.ts";
import { useServices } from "../app/services.ts";
import { shortTime } from "../data/display.ts";
import { disable, enable, type PushState, state } from "../push/webPush.ts";
import { Icon } from "../ui/Icon.tsx";
import styles from "./Settings.module.css";

/** Settings "Dieser Browser und Geräte" (docs/web-designs screen 8), the first part of them. */
export function Settings() {
  const services = useServices();
  const [devices, setDevices] = useState<DeviceResponse[]>();
  const [error, setError] = useState<string>();
  const me = services.session.current?.user;

  const load = () =>
    services.session.call<DevicesResponse>("/v1/me/devices").then(
      (r) => setDevices(r.devices),
      () => setError("Die Geräte ließen sich nicht laden."),
    );
  useEffect(() => void load(), []); // eslint-disable-line react-hooks/exhaustive-deps

  async function signOut(id: string) {
    if (!confirm("Dieses Gerät abmelden? Es muss sich dann neu anmelden.")) return;
    await services.session.call(`/v1/me/devices/${id}`, { method: "DELETE" }).catch(() => setError("Das Abmelden hat nicht geklappt."));
    void load();
  }

  async function signOutOthers() {
    if (!confirm("Alle anderen Geräte abmelden? Auch dein Handy muss sich dann neu anmelden.")) return;
    await services.session.call("/v1/me/devices/sign-out-others", { method: "POST" }).catch(() => setError("Das Abmelden hat nicht geklappt."));
    void load();
  }

  return (
    <main className={styles.page}>
      <h1>Einstellungen</h1>
      <div className={styles.columns}>
        <section>
          <h2>Konto</h2>
          <div className={styles.row}>
            <Icon name="mail" />
            <div>
              <div>{me?.display_name ?? "Ohne Namen"}</div>
              <small>{me?.email}</small>
            </div>
          </div>
          <div className={styles.actions}>
            <button className={styles.outlined} onClick={() => void services.session.logout()}>
              <Icon name="logout" size={18} /> Abmelden
            </button>
          </div>
          <h2>Dieser Browser</h2>
          <Notifications />
          <div className={styles.row}>
            <Icon name="keyboard" />
            <div>
              <div>Tastenkürzel</div>
              <small>
                <kbd>⌘ K</kbd> Suchen · <kbd>Enter</kbd> Senden · <kbd>Shift</kbd> + <kbd>Enter</kbd> neue Zeile
              </small>
            </div>
          </div>
        </section>
        <section>
          <h2>Geräte</h2>
          <small className={styles.lead}>Überall, wo du angemeldet bist.</small>
          {error && <p className={styles.error}>{error}</p>}
          {!devices && !error && <p className={styles.lead}>Wird geladen …</p>}
          {devices?.map((d) => (
            <div key={d.id} className={styles.row}>
              <Icon name={d.platform === "web" ? "laptop" : "phone"} />
              <div>
                <div>
                  {d.name ?? (d.platform === "web" ? "Browser" : "Handy")}
                  {d.client_build ? ` · App, Build ${d.client_build}` : ""}
                </div>
                <small>{d.current ? "dieses Gerät" : `zuletzt aktiv ${shortTime(d.last_seen_at)}`}</small>
              </div>
              {d.current ? (
                <span className={styles.here}>hier</span>
              ) : (
                <button className={styles.text} onClick={() => void signOut(d.id)}>
                  Abmelden
                </button>
              )}
            </div>
          ))}
          {devices && devices.length > 1 && (
            <div className={styles.actions}>
              <button className={styles.danger} onClick={() => void signOutOthers()}>
                <Icon name="logout" size={18} /> Alle anderen abmelden
              </button>
            </div>
          )}
          <div className={styles.card}>
            <b>
              <Icon name="phone" /> Nur in der App
            </b>
            <small>Das kann ein Browser nicht – dafür ist dein Handy da:</small>
            <ul>
              <li>Wecker, die klingeln, bis du sie ausmachst</li>
              <li>Anrufe auf dem Sperrbildschirm annehmen</li>
              <li>Live-Standort, wenn Voxie zu ist</li>
              <li>Abspielen über die Hörmuschel</li>
            </ul>
          </div>
        </section>
      </div>
    </main>
  );
}

const PUSH_TEXT: Record<PushState, string> = {
  on: "Auch wenn Voxie nicht offen ist. Nur wenn kein Voxie-Tab sichtbar ist, ohne Nachrichtentext.",
  off: "Neue Nachrichten melden, wenn Voxie nicht offen ist.",
  denied: "Der Browser blockiert Benachrichtigungen für Voxie. Erlaube sie über das Schloss-Symbol in der Adressleiste.",
  untrusted: "Nur in einem Browser, dem du vertraust: abmelden und mit „Diesem Browser vertrauen“ neu anmelden.",
  unsupported: "Dieser Browser kann keine Benachrichtigungen. Auf dem iPhone: Voxie zum Home-Bildschirm hinzufügen.",
  unconfigured: "Der Server schickt noch keine Benachrichtigungen an Browser. Dein Handy meldet sich weiter.",
};

/** Web Push on or off for this browser (docs/web-designs screen 8). */
function Notifications() {
  const { session } = useServices();
  const [push, setPush] = useState<PushState>();
  const [busy, setBusy] = useState(false);
  useEffect(() => void state(session).then(setPush, () => setPush("unsupported")), [session]);
  async function toggle() {
    setBusy(true);
    try {
      setPush(push === "on" ? await disable(session) : await enable(session));
    } catch {
      setPush(await state(session).catch(() => "unsupported" as const));
    } finally {
      setBusy(false);
    }
  }
  const switchable = push === "on" || push === "off";
  return (
    <div className={styles.row}>
      <Icon name="bell" />
      <div>
        <div>Benachrichtigungen</div>
        <small>{push ? PUSH_TEXT[push] : "…"}</small>
      </div>
      {switchable && (
        <button className={push === "on" ? `${styles.switch} ${styles.switchOn}` : styles.switch} role="switch" aria-checked={push === "on"} aria-label="Benachrichtigungen" disabled={busy} onClick={() => void toggle()}>
          <span />
        </button>
      )}
    </div>
  );
}
